import { z } from 'zod';

/**
 * Wave 25 — Expiring, read-only share links for Medição and DRE.
 *
 * A PM mints a URL, hands it to a client, client opens it without a
 * login and sees a *specific report as the PM saw it*. Unlike approval
 * tokens (single-use, entity-bound), share links are multi-view and
 * surface-bound. That's why they live in their own table.
 */

export const shareLinkSurfaceSchema = z.enum(['medicao', 'dre']);
export type ShareLinkSurface = z.infer<typeof shareLinkSurfaceSchema>;

/** YYYY-MM — the month of the boletim snapshot. */
export const medicaoParamsSchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/u, 'month deve estar no formato YYYY-MM'),
});
export type MedicaoShareParams = z.infer<typeof medicaoParamsSchema>;

/** YYYY-MM-DD dates; either side can be null → "all time" on that side. */
export const dreParamsSchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u, 'from deve estar no formato YYYY-MM-DD')
    .nullable(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u, 'to deve estar no formato YYYY-MM-DD')
    .nullable(),
});
export type DreShareParams = z.infer<typeof dreParamsSchema>;

/** Discriminated union of valid param shapes by surface. */
export const shareLinkParamsSchema = z.union([
  medicaoParamsSchema,
  dreParamsSchema,
]);
export type ShareLinkParams = z.infer<typeof shareLinkParamsSchema>;

/** Preset TTLs only. Keeps audit simple and prevents accidental year-long links. */
export const shareLinkTtlSchema = z.union([
  z.literal(7),
  z.literal(14),
  z.literal(30),
]);
export type ShareLinkTtlDays = z.infer<typeof shareLinkTtlSchema>;

export const createShareLinkRequestSchema = z
  .object({
    surface: shareLinkSurfaceSchema,
    params: shareLinkParamsSchema,
    ttlDays: shareLinkTtlSchema.default(14),
  })
  .superRefine((val, ctx) => {
    if (val.surface === 'medicao') {
      const ok = medicaoParamsSchema.safeParse(val.params);
      if (!ok.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'params inválidos para surface=medicao (esperado {month})',
          path: ['params'],
        });
      }
    } else {
      const ok = dreParamsSchema.safeParse(val.params);
      if (!ok.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'params inválidos para surface=dre (esperado {from,to})',
          path: ['params'],
        });
      }
    }
  });
export type CreateShareLinkRequest = z.infer<
  typeof createShareLinkRequestSchema
>;

/**
 * Mint response — the RAW token is returned here and ONLY here. After
 * this response, the token can never be retrieved again. The DB stores
 * only its SHA-256 hash.
 */
export const createShareLinkResponseSchema = z.object({
  id: z.string(),
  token: z.string(),
  url: z.string(),
  surface: shareLinkSurfaceSchema,
  params: shareLinkParamsSchema,
  expiresAt: z.string(),
  createdAt: z.string(),
});
export type CreateShareLinkResponse = z.infer<
  typeof createShareLinkResponseSchema
>;

/** Row shape returned by the list endpoint — raw token is never included. */
export const shareLinkSummarySchema = z.object({
  id: z.string(),
  surface: shareLinkSurfaceSchema,
  params: shareLinkParamsSchema,
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  viewCount: z.number().int().min(0),
  lastViewedAt: z.string().nullable(),
  createdAt: z.string(),
  issuedByName: z.string().nullable(),
});
export type ShareLinkSummary = z.infer<typeof shareLinkSummarySchema>;

export const listShareLinksResponseSchema = z.array(shareLinkSummarySchema);
export type ListShareLinksResponse = z.infer<typeof listShareLinksResponseSchema>;

export const revokeShareLinkResponseSchema = z.object({
  id: z.string(),
  revokedAt: z.string(),
});
export type RevokeShareLinkResponse = z.infer<
  typeof revokeShareLinkResponseSchema
>;

/** Public resolve — minimal descriptor to render the public landing. */
export const resolveShareLinkRequestSchema = z.object({
  token: z.string().min(16).max(256),
});
export type ResolveShareLinkRequest = z.infer<
  typeof resolveShareLinkRequestSchema
>;

export const resolveShareLinkResponseSchema = z.object({
  surface: shareLinkSurfaceSchema,
  params: shareLinkParamsSchema,
  projectName: z.string(),
  expiresAt: z.string(),
});
export type ResolveShareLinkResponse = z.infer<
  typeof resolveShareLinkResponseSchema
>;

// ---------------------------------------------------------------------
// Public data payloads — deliberate subsets of the internal schemas.
//
// The public surface is MASKED:
//   - no tenant/user/internal ids,
//   - no supplier names or CNPJs (leaks competitive info),
//   - no payment-settled totals (cash movement is internal),
//   - no line-item cost breakdown on DRE,
//   - no PR/PO attachments.
// ---------------------------------------------------------------------

export const sharedMedicaoItemSchema = z.object({
  stageName: z.string(),
  percentBilled: z.number().min(0).max(100),
  subtotalCents: z.number().int().nonnegative(),
});
export type SharedMedicaoItem = z.infer<typeof sharedMedicaoItemSchema>;

export const sharedMedicaoMeasurementSchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  status: z.enum(['approved', 'sent', 'paid']),
  totalCents: z.number().int().nonnegative(),
  approvedByName: z.string().nullable(),
  notes: z.string().nullable(),
  items: z.array(sharedMedicaoItemSchema),
});
export type SharedMedicaoMeasurement = z.infer<
  typeof sharedMedicaoMeasurementSchema
>;

export const sharedMedicaoPayloadSchema = z.object({
  projectName: z.string(),
  clientName: z.string(),
  month: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  measurements: z.array(sharedMedicaoMeasurementSchema),
  totals: z.object({
    measurementTotalCents: z.number().int().nonnegative(),
    grandTotalCents: z.number().int().nonnegative(),
  }),
  generatedAt: z.string(),
  expiresAt: z.string(),
});
export type SharedMedicaoPayload = z.infer<typeof sharedMedicaoPayloadSchema>;

export const sharedDrePayloadSchema = z.object({
  projectName: z.string(),
  period: z.object({
    from: z.string().nullable(),
    to: z.string().nullable(),
  }),
  revenueTotalCents: z.number().int().nonnegative(),
  costTotalCents: z.number().int(),
  grossProfitCents: z.number().int(),
  marginPercent: z.number(),
  generatedAt: z.string(),
  expiresAt: z.string(),
});
export type SharedDrePayload = z.infer<typeof sharedDrePayloadSchema>;
